import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Crown } from "lucide-react";
import type { CustomerCategory, CustomerForm } from "./types";

export function CategoryFilterBadges({
  categories,
  categoryFilter,
  onFilterChange,
}: {
  categories: CustomerCategory[];
  categoryFilter: string;
  onFilterChange: (value: string) => void;
}) {
  const membershipCategories = useMemo(
    () => categories.filter((c) => c.membership_config),
    [categories]
  );

  if (membershipCategories.length === 0) return null;

  return (
    <div className="flex gap-1.5 flex-wrap">
      <Badge
        variant={categoryFilter === "all" ? "default" : "outline"}
        className="cursor-pointer text-xs"
        onClick={() => onFilterChange("all")}
      >
        Alla
      </Badge>
      {membershipCategories.map((mc) => {
        const config = mc.membership_config as Record<string, unknown>;
        return (
          <Badge
            key={mc.id}
            variant={categoryFilter === mc.id ? "default" : "outline"}
            className="cursor-pointer text-xs"
            onClick={() => onFilterChange(mc.id)}
          >
            <Crown className="h-3 w-3 mr-1" />
            {config?.name || mc.name}
          </Badge>
        );
      })}
    </div>
  );
}

// --- Shared form component ---

export function CustomerFormComponent({
  form,
  setForm,
  emailDisabled,
  onSubmit,
  submitLabel,
  loading,
  onCancel,
  categories = [],
}: {
  form: CustomerForm;
  setForm: (f: CustomerForm) => void;
  emailDisabled: boolean;
  onSubmit: () => void;
  submitLabel: string;
  loading: boolean;
  onCancel?: () => void;
  categories?: CustomerCategory[];
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label>E-post *</Label>
        <Input
          type="email"
          value={form.email}
          disabled={emailDisabled}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="kund@example.com"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Namn</Label>
          <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        </div>
        <div>
          <Label>Telefon</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Nivå</Label>
          <Select value={String(form.level)} onValueChange={(v) => setForm({ ...form, level: Number(v) })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Spelartyp</Label>
          <Select value={form.player_type} onValueChange={(v) => setForm({ ...form, player_type: v })}>
            <SelectTrigger><SelectValue placeholder="–" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Offensiv">Offensiv</SelectItem>
              <SelectItem value="Defensiv">Defensiv</SelectItem>
              <SelectItem value="Allround">Allround</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Hand</Label>
          <Select value={form.preferred_hand} onValueChange={(v) => setForm({ ...form, preferred_hand: v })}>
            <SelectTrigger><SelectValue placeholder="–" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Höger">Höger</SelectItem>
              <SelectItem value="Vänster">Vänster</SelectItem>
              <SelectItem value="Båda">Båda</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {categories.length > 0 && (
        <div>
          <Label>Kundkategori</Label>
          <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Standard" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Standard</SelectItem>
              {categories.filter(c => !c.is_default).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label>Anteckningar</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          placeholder="Intern anteckning…"
        />
      </div>
      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>Avbryt</Button>
        )}
        <Button size="sm" onClick={onSubmit} disabled={loading || !form.email.trim()}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
